import { Config } from 'config/envConfig';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Modal,
    Button,
    Input,
    Dropdown,
    alert,
    AlertType,
    TextArea,
    Toggle,
} from '@quyva/shared-ui';
import {
    faPlus,
    faXmark,
    faPen,
    faRefresh,
    faFolderPlus,
    faFileUpload,
    faFilePdf,
    faCircleXmark,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { getUrl } from '../pdfDisplay/utils.js';
import axios from 'axios';
import { toSentenceCase, RepoType, isValidNamingRegex } from '@quyva/common';
import { useAuth } from '../../context/AuthContext';
import { LoadingView } from '@quyva/shared-ui';

const Mode = {
    ADD: 'add',
    EDIT: 'edit',
};

const normalizeDescription = (description) => {
    if (!description) return '';
    const cleaned = description.replace(/\s\s+/g, ' ');
    return cleaned;
};

export const OptionalConfigFields = Object.freeze([
    'instructions',
    'add_uploaded_files_as_attachment',
    'headers',
    'query_params',
]);

const CompanyDocManager = ({ value, onChange }) => {
    const [categories, setCategories] = useState([]);
    const [files, setFiles] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [deletingCategory, setDeletingCategory] = useState(null); // category name
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [deletingFile, setDeletingFile] = useState(null); // file name
    const { user } = useAuth();

    const getBaseUrl = () =>
        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/tool_manager`;
    // 1. Fetch Categories
    const fetchCategories = async () => {
        try {
            const formData = new FormData();
            formData.append('owner_id', user.email);
            const response = await axios.post(
                getUrl(`${getBaseUrl()}/company_docs/get_categories`),
                formData
            );
            setCategories(response.data.categories || []);
        } catch (err) {
            console.error('Failed to fetch categories', err);
            alert(
                AlertType.ERROR,
                err.response?.data?.detail || 'Failed to fetch categories'
            );
        }
    };

    useEffect(() => {
        if (user?.email) fetchCategories();
    }, [user]);

    // 2. Fetch Files when category changes
    const fetchFiles = async () => {
        if (!value) {
            setFiles([]);
            return;
        }
        setIsLoadingFiles(true);
        try {
            const formData = new FormData();
            formData.append('owner_id', user.email);
            formData.append('category_name', value);
            const response = await axios.post(
                getUrl(`${getBaseUrl()}/company_docs/list_files`),
                formData
            );
            setFiles(response.data.files || []);
        } catch (err) {
            console.error('Failed to fetch files', err);
            alert(
                AlertType.ERROR,
                err.response?.data?.detail || 'Failed to fetch files'
            );
            setFiles([]);
        } finally {
            setIsLoadingFiles(false);
        }
    };
    useEffect(() => {
        fetchFiles();
    }, [value, user]);

    // 3. Create New Category
    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return;
        setIsCreatingCategory(true);
        try {
            const formData = new FormData();
            formData.append('owner_id', user.email);
            formData.append('category_name', newCategoryName);
            await axios.post(
                getUrl(`${getBaseUrl()}/company_docs/create_category`),
                formData
            );
            alert(AlertType.SUCCESS, `Category "${newCategoryName}" created`);
            await fetchCategories();
            onChange(newCategoryName);
            setIsCreating(false);
            setNewCategoryName('');
        } catch (err) {
            alert(
                AlertType.ERROR,
                err.response?.data?.detail || 'Failed to create category'
            );
        } finally {
            setIsCreatingCategory(false);
        }
    };

    // 4. Upload File
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !value) return;
        setIsUploadingFile(true);
        try {
            const formData = new FormData();
            formData.append('owner_id', user.email);
            formData.append('category_name', value);
            formData.append('file', file);
            await axios.post(
                getUrl(`${getBaseUrl()}/company_docs/upload_file`),
                formData
            );
            alert(AlertType.SUCCESS, 'File uploaded successfully');
            fetchFiles();
        } catch (err) {
            if (err.response?.status === 409) {
                alert(
                    AlertType.ERROR,
                    err.response.data?.detail ||
                        'File with same name already exists in this category.'
                );
            } else {
                alert(AlertType.ERROR, 'Failed to upload file');
            }
        } finally {
            setIsUploadingFile(false);
            e.target.value = null;
        }
    };

    // 5. Delete File
    const handleDeleteFile = async (fileName) => {
        setDeletingFile(fileName);
        try {
            const formData = new FormData();
            formData.append('owner_id', user.email);
            formData.append('category_name', value);
            formData.append('file_name', fileName);
            await axios.post(
                getUrl(`${getBaseUrl()}/company_docs/delete_file`),
                formData
            );
            alert(AlertType.SUCCESS, 'File deleted');
            fetchFiles();
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to delete file');
        } finally {
            setDeletingFile(null);
        }
    };

    // 6. Delete Category
    const handleDeleteCategory = async (category) => {
        setDeletingCategory(category);
        try {
            const formData = new FormData();
            formData.append('owner_id', user.email);
            formData.append('category_name', category);
            await axios.post(
                getUrl(`${getBaseUrl()}/company_docs/delete_category`),
                formData
            );
            alert(AlertType.SUCCESS, `Category "${category}" deleted`);
            fetchCategories();

            if (value === category) {
                onChange('');
                setFiles([]);
            }
        } catch (err) {
            alert(AlertType.ERROR, 'Failed to delete category');
        } finally {
            setDeletingCategory(null);
        }
    };

    const categoryOptions = categories.map((categoryName) => ({
        key: categoryName,
        label: (
            <div className='flex justify-between items-center gap-2'>
                <span>{categoryName}</span>
                <Button
                    icon={faCircleXmark}
                    type='danger'
                    hoverText='Delete Category'
                    disabled={deletingCategory === categoryName}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(categoryName);
                    }}
                />
            </div>
        ),
        action: () => onChange(categoryName),
    }));

    return (
        <div className='flex flex-col gap-3 p-3 border rounded-xl bg-backgrounds-white'>
            <div className='flex items-center justify-between gap-4'>
                <div className='flex items-center gap-4 flex-1'>
                    <label className='text-base font-medium text-texts-secondary300 whitespace-nowrap'>
                        Category
                    </label>
                    <Dropdown
                        dropDownList={categoryOptions}
                        buttonText={value || 'Select Category'}
                        disabled={isCreatingCategory}
                        buttonHoverText='Select Document Category'
                        showButtonText
                        buttonClassName='min-w-[180px] w-full border rounded-xl text-left justify-between'
                        listClassName='max-h-40 overflow-y-auto'
                    />
                </div>
                <Button
                    icon={faPlus}
                    type='secondary'
                    hoverText='Add New Category'
                    disabled={isCreatingCategory}
                    onClick={() => setIsCreating(!isCreating)}
                />
            </div>

            {isCreating && (
                <div className='flex items-center gap-2 animate-fadeIn'>
                    <Input
                        placeholder='New Category Name'
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        className='flex-1'
                        disabled={isCreatingCategory}
                    />
                    <Button
                        icon={faFolderPlus}
                        type='success'
                        hoverText='Create Category'
                        onClick={handleCreateCategory}
                        disabled={!newCategoryName || isCreatingCategory}
                    />
                </div>
            )}

            {value && (
                <div className='mt-2 border-t pt-2'>
                    <div className='flex justify-between items-center mb-2'>
                        <label className='text-base font-medium text-texts-secondary300'>
                            Documents in '{value}'
                        </label>
                        <div className='relative'>
                            <input
                                type='file'
                                accept='application/pdf'
                                id='doc-upload'
                                className='hidden'
                                onChange={handleFileUpload}
                            />

                            <Button
                                text='Upload'
                                icon={faFileUpload}
                                type='success'
                                hoverText='Upload new document'
                                onClick={() =>
                                    document
                                        .getElementById('doc-upload')
                                        .click()
                                }
                                disabled={isUploadingFile}
                            />
                        </div>
                    </div>

                    <div className='bg-backgrounds-slate rounded-lg p-2 min-h-[60px] max-h-[150px] overflow-y-auto'>
                        {isLoadingFiles ? (
                            <div className='flex justify-center items-center h-[80px]'>
                                <LoadingView loadingText='Loading documents...' />
                            </div>
                        ) : files.length === 0 ? (
                            <span className='text-xs text-texts-secondary200 italic'>
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <span> No documents found in this folder.</span>
                            </span>
                        ) : (
                            <ul className='flex flex-col gap-1'>
                                {files.map((file, idx) => (
                                    <li
                                        key={idx}
                                        className='text-sm flex items-center justify-between gap-2 text-texts-secondary300'
                                    >
                                        <div className='flex items-center gap-2 min-w-0'>
                                            <FontAwesomeIcon
                                                icon={faFilePdf}
                                                className='text-texts-red400'
                                            />
                                            <span
                                                className='truncate'
                                                title={file}
                                            >
                                                {file}
                                            </span>
                                        </div>

                                        <Button
                                            icon={faCircleXmark}
                                            type='danger'
                                            hoverText='Delete file'
                                            onClick={() =>
                                                handleDeleteFile(file)
                                            }
                                            disabled={deletingFile === file}
                                        ></Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default function AddInstanceDialog({
    open,
    onClose,
    onCreate,
    onUpdate,
    tools,
    mode = Mode.ADD, // "add" or "edit"
    initialData,
    activeTool,
    user,
    instances = [],
}) {
    const [toolId, setToolId] = useState(activeTool ? activeTool.id : '');
    const [instanceName, setInstanceName] = useState('');
    const [configValues, setConfigValues] = useState({});
    const [repos, setRepos] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [layouts, setLayouts] = useState([]);
    const [forms, setForms] = useState([]);
    const [autoGeneratedDescription, setAutoGeneratedDescription] =
        useState('');
    const [displayDescription, setDisplayDescription] = useState('');
    const [fileToUpload, setFileToUpload] = useState(null);
    const [existingFileName, setExistingFileName] = useState('');

    const selectedTool = tools.find(
        (toolItem) => toolItem.id === toolId || toolItem.name === toolId
    );

    const extractFileName = (path = '') => {
        if (typeof path !== 'string') return '';
        const parts = path.split(/[/\\]/);
        return parts.length ? parts[parts.length - 1] : '';
    };

    // Initialize form data and fetch description once
    useEffect(() => {
        if (open) {
            if (mode === Mode.EDIT && initialData) {
                setToolId(initialData.tool_name);
                setInstanceName(initialData.instance_name);
                setConfigValues(initialData.config || {});
                setDisplayDescription(
                    normalizeDescription(initialData.description || '')
                );
                setFileToUpload(null);
                // Extract existing file name from config if present
                const filePath =
                    initialData.config?.file_upload ||
                    initialData.config?.file ||
                    '';
                if (filePath) {
                    setExistingFileName(extractFileName(filePath));
                } else {
                    setExistingFileName('');
                }
            } else {
                setToolId(activeTool ? activeTool.id : '');
                setInstanceName('');
                setConfigValues({});
                fetchDescription();
                setFileToUpload(null);
                setExistingFileName('');
            }
        }
    }, [open, mode, activeTool, initialData]);

    // Chat Repos API
    const fetchAllChatRepos = async (user) => {
        if (!user) throw new Error('User not found');
        const url = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/getAllChatsRepos/${user.email}`
        );
        const encodedToken = encodeURIComponent(user.token);
        const response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: encodedToken,
            },
        });
        const data = response.data;
        const normalRepos = data.Normal.filter(
            (i) =>
                i.repotype !== RepoType.MASTER_AGENT &&
                i.repotype !== RepoType.GLOBAL
        );
        const sharedRepos = (data.Shared || []).filter(
            (i) => i.repotype !== RepoType.MASTER_AGENT
        );
        const allRepos = [...normalRepos, ...sharedRepos];
        return allRepos;
    };

    // Templates API
    const fetchAllTemplates = async (user) => {
        if (!user) throw new Error('User not found');
        const tempUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/gettemplate?id=${user.email}`
        );
        const response = await axios.get(tempUrl);
        return response.data || [];
    };

    // Forms API
    const fetchAllForms = async (user) => {
        if (!user) throw new Error('User not found');
        const formsUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/forms/list?email=${encodeURIComponent(
                user.email
            )}`
        );
        const response = await axios.get(formsUrl);
        return response.data?.forms || [];
    };

    // Layouts API
    const fetchLayoutsList = async (templateName) => {
        try {
            if (!templateName) return [];
            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/getLayoutsList?email=${user.email}&template=${templateName}`
            );
            const encodedToken = encodeURIComponent(user.token);
            const response = await axios.get(dynamicUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: encodedToken,
                },
            });
            return response.data.layoutsList || [];
        } catch (err) {
            alert(AlertType.ERROR, err.message || 'Failed to fetch layouts');
            return [];
        }
    };

    useEffect(() => {
        const loadRepos = async () => {
            try {
                const repoData = await fetchAllChatRepos(user);
                setRepos(repoData);
            } catch (err) {
                alert(
                    AlertType.ERROR,
                    'Failed to load repositories. Please try again.'
                );
            }
        };
        loadRepos();
    }, [user]);

    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const templateData = await fetchAllTemplates(user);
                setTemplates(templateData);
            } catch (err) {
                alert(
                    AlertType.ERROR,
                    'Failed to load templates. Please try again.'
                );
            }
        };
        loadTemplates();
    }, [user]);

    useEffect(() => {
        const loadForms = async () => {
            try {
                const formData = await fetchAllForms(user);
                setForms(formData);
            } catch (err) {
                alert(
                    AlertType.ERROR,
                    'Failed to load forms. Please try again.'
                );
            }
        };
        loadForms();
    }, [user]);

    useEffect(() => {
        const loadLayouts = async () => {
            try {
                const selectedTemplate = configValues?.template_name;
                if (selectedTemplate) {
                    const list = await fetchLayoutsList(selectedTemplate);
                    setLayouts(list);
                }
            } catch (err) {
                alert(
                    AlertType.ERROR,
                    err.message ||
                        'Failed to load layouts list. Please try again.'
                );
            }
        };
        loadLayouts();
    }, [configValues?.template_name]);

    // Initialize configValues for selected tool

    useEffect(() => {
        if (!selectedTool?.config) return;
        if (mode === Mode.EDIT) return;

        const initialConfig = {};
        selectedTool.config.forEach((configField) => {
            if (configField.type === 'toggle') {
                initialConfig[configField.name] = 'False';
            } else {
                initialConfig[configField.name] = '';
            }
        });
        setConfigValues(initialConfig);
    }, [selectedTool, mode]);

    const fetchDescription = async () => {
        try {
            if (toolId == '' || !toolId) return;
            const formData = new FormData();
            formData.append('instance_name', instanceName || 'New Instance');
            formData.append('tool_name', selectedTool?.name || toolId);
            formData.append('owner_id', user.email);
            formData.append('config', JSON.stringify(configValues || {}));

            const response = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/tool_manager/get_instance_description`
                ),
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            const fetchedDesc = normalizeDescription(
                response.data?.description || ''
            );
            setAutoGeneratedDescription(fetchedDesc);
            setDisplayDescription(fetchedDesc);
        } catch (err) {
            console.error('Error fetching description:', err);
            alert(
                AlertType.ERROR,
                'Failed to fetch instance description. Please try again.'
            );
            setAutoGeneratedDescription('');
            setDisplayDescription('');
        }
    };

    useEffect(() => {
        if (!toolId || !user?.email) return;
        if (mode === Mode.EDIT) return;
        fetchDescription();
    }, [toolId]);

    // Handle config changes (no live updates)
    const handleConfigChange = (field, value) => {
        setConfigValues((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'template_name') {
                updated['layout_name'] = '';
            }
            return updated;
        });
    };

    const handleInstanceNameChange = (e) => {
        const value = e.target.value;
        if (!isValidNamingRegex(value)) {
            return;
        }
        setInstanceName(value);
    };

    const handleFileUpload = (field, file) => {
        setFileToUpload((prev) => ({ ...prev, [field]: file }));
        setExistingFileName(file.name);
    };

    const validateConfigFields = (showAlert = false) => {
        if (!selectedTool?.config) return true;

        for (const configField of selectedTool.config) {
            if (OptionalConfigFields.includes(configField.name.toLowerCase()))
                continue;

            if (configField.type === 'file_uploader') {
                const uploadedFile =
                    fileToUpload && configField?.name
                        ? fileToUpload[configField.name]
                        : undefined;
                const filePresent =
                    uploadedFile || (mode === Mode.EDIT && existingFileName);

                if (!filePresent) {
                    if (showAlert)
                        alert(
                            AlertType.ERROR,
                            `Please upload a file for "${configField.name}".`
                        );
                    return false;
                }
                continue;
            }
            const value = configValues[configField.name];
            if (value === undefined || value === null || value === '') {
                if (showAlert)
                    alert(
                        AlertType.ERROR,
                        `Please fill in the "${configField.name}" field.`
                    );
                return false;
            }
        }
        return true;
    };

    const isInstanceNameUnique = (name) => {
        if (mode === Mode.EDIT && name === initialData?.instance_name)
            return true;
        return !instances.some(
            (inst) => inst.instance_name.toLowerCase() === name.toLowerCase()
        );
    };

    const handleSave = () => {
        if (!toolId || !instanceName) {
            alert(
                AlertType.ERROR,
                'Please select a tool and enter an instance name.'
            );
            return;
        }

        if (!isInstanceNameUnique(instanceName)) {
            alert(AlertType.ERROR, 'Instance name must be unique.');
            return;
        }

        if (!validateConfigFields(true)) return;

        if (mode === Mode.ADD) {
            onCreate(selectedTool?.name || toolId, {
                instance_name: instanceName,
                config: configValues,
                description: normalizeDescription(displayDescription),
                file: fileToUpload,
            });
        } else {
            onUpdate(initialData.id, {
                instance_name: instanceName,
                tool_name: toolId,
                config: configValues,
                description: normalizeDescription(displayDescription),
                file: fileToUpload,
                ...(!fileToUpload && existingFileName
                    ? { existingFile: existingFileName }
                    : {}),
            });
        }
        onClose();
    };

    // Dropdown items for selecting a base tool
    const toolDropdownItems = tools
        .filter((toolItem) => toolItem.id !== 'all')
        .map((toolItem) => ({
            key: toolItem.id,
            label: toSentenceCase(toolItem.name),
            action: () => setToolId(toolItem.id),
        }));

    const getDropdownOptions = (configField) => {
        if (configField.type === 'template_selector') {
            return (templates || []).map((templateItem) => ({
                key: templateItem.filename,
                label: templateItem.filename,
                action: () =>
                    handleConfigChange(configField.name, templateItem.filename),
            }));
        }
        if (configField.type === 'repo_selector') {
            return (repos || []).map((repo) => ({
                key: repo.repoid,
                label: repo.name,
                action: () =>
                    handleConfigChange(configField.name, {
                        repo_id: repo.repoid,
                        repo_name: repo.name,
                    }),
            }));
        }
        if (configField.type === 'layout_selector') {
            return (layouts || []).map((layout) => ({
                key: layout.name,
                label: layout.name,
                action: () => handleConfigChange(configField.name, layout.name),
            }));
        }
        if (configField.type === 'form_selector') {
            return (forms || []).map((form) => ({
                key: form.form_id,
                label: form.form_name,
                action: () =>
                    handleConfigChange(configField.name, {
                        form_id: form.form_id,
                        form_name: form.form_name,
                    }),
            }));
        }
        return (configField.options || []).map((optionItem) => ({
            key: optionItem,
            label: optionItem,
            action: () => handleConfigChange(configField.name, optionItem),
        }));
    };

    const renderConfigField = (configField) => {
        const value = configValues[configField.name] || '';

        switch (configField.type) {
            case 'text':
            case 'string':
                return (
                    <Input
                        placeholder={configField.description}
                        value={value}
                        onChange={(e) =>
                            handleConfigChange(configField.name, e.target.value)
                        }
                        className='p-2 border rounded-xl'
                    />
                );
            case 'textarea':
                return (
                    <TextArea
                        className='min-h-[70px] resize-none p-2 focus:outline-backgrounds-primary200 border rounded-xl'
                        placeholder={configField.description}
                        value={value}
                        onChange={(e) =>
                            handleConfigChange(configField.name, e.target.value)
                        }
                        rows={3}
                    />
                );
            case 'repo_selector':
            case 'template_selector':
            case 'layout_selector':
            case 'form_selector':
            case 'dropdown':
                const options = getDropdownOptions(configField);
                let buttonText = value || 'Select Option';
                if (configField.type === 'repo_selector')
                    buttonText = value?.repo_name || 'Select Repository';
                if (configField.type === 'template_selector')
                    buttonText = value || 'Select Template';
                if (configField.type === 'layout_selector')
                    buttonText = value || 'Select Layout';
                if (configField.type === 'form_selector')
                    buttonText = value?.form_name || 'Select Form';
                return (
                    <Dropdown
                        dropDownList={options}
                        buttonText={buttonText}
                        buttonClassName='min-w-[150px] border px-3 py-2 text-base rounded-xl bg-backgrounds-white'
                        showButtonText
                    />
                );
            case 'category_doc_manager':
                return (
                    <CompanyDocManager
                        value={value}
                        onChange={(val) =>
                            handleConfigChange(configField.name, val)
                        }
                    />
                );
            case 'file_uploader':
                return (
                    <div className='flex flex-col gap-1'>
                        <input
                            type='file'
                            accept='application/pdf'
                            className='bg-backgrounds-white w-full p-2 rounded-xl border cursor-pointer'
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    if (file.type !== 'application/pdf') {
                                        alert(
                                            AlertType.ERROR,
                                            'Only PDF files are allowed.'
                                        );
                                        e.target.value = '';
                                        return;
                                    }
                                    handleFileUpload(configField.name, file);
                                } else {
                                    setFileToUpload((prev) => ({
                                        ...prev,
                                        [configField.name]: null,
                                    }));
                                    setExistingFileName('');
                                }
                            }}
                            title={configField.description}
                        />
                        {mode === Mode.EDIT &&
                            existingFileName &&
                            !fileToUpload && (
                                <span className='text-sm text-texts-secondary300 italic'>
                                    Current file: {existingFileName}. Upload new
                                    file to update.
                                </span>
                            )}
                    </div>
                );
            case 'toggle':
                return (
                    <Toggle
                        toggleText={toSentenceCase(configField.name)}
                        toggleTextClassName='w-auto font-medium text-texts-secondary300 mr-2'
                        checked={configValues[configField.name] === 'True'}
                        onChange={(checked) => {
                            const isChecked = checked ? 'True' : 'False';
                            handleConfigChange(configField.name, isChecked);
                        }}
                        className='flex items-center gap-3'
                    />
                );

            default:
                return <div>Unknown config type</div>;
        }
    };

    const isUnchanged =
        instanceName === initialData?.instance_name &&
        toolId === initialData?.tool_name &&
        JSON.stringify(configValues) ===
            JSON.stringify(initialData?.config || {}) &&
        normalizeDescription(displayDescription) ===
            normalizeDescription(initialData?.description || '') &&
        !fileToUpload;

    return (
        <Modal
            title={mode === Mode.ADD ? 'Add New Instance' : 'Edit Instance'}
            titleIcon={mode === Mode.ADD ? faPlus : faPen}
            open={open}
            onClose={onClose}
        >
            <div className='w-[60vw] min-w-[500px] flex flex-col px-6 py-6 rounded-xl bg-backgrounds-slate text-texts-secondary300'>
                <div className='flex flex-col gap-2'>
                    <div className='flex flex-col p-2'>
                        <label className='text-base font-medium mb-1 text-texts-secondary300'>
                            Instance Name{' '}
                            <span className='text-texts-red400'>*</span>
                        </label>
                        <Input
                            placeholder='Instance Name'
                            value={instanceName}
                            onChange={handleInstanceNameChange}
                            disabled={mode === Mode.EDIT}
                        />
                    </div>

                    <div className='w-full p-2 flex space-between items-center gap-5'>
                        <label className='text-base font-medium mb-1 text-texts-secondary300'>
                            Tool <span className='text-texts-red400'>*</span>
                        </label>
                        <Dropdown
                            dropDownList={toolDropdownItems}
                            buttonText={toSentenceCase(
                                selectedTool ? selectedTool.name : 'Select Tool'
                            )}
                            showButtonText
                            disabled={mode === Mode.EDIT || !!activeTool}
                            listClassName='max-h-[200px] overflow-y-auto scrollbar-hide'
                        />
                    </div>

                    {selectedTool && (
                        <div className='flex flex-col p-2 gap-2'>
                            <div className='flex gap-2 items-center mb-1'>
                                <label className='text-base font-medium text-texts-secondary300'>
                                    Description
                                </label>
                                <Button
                                    size='xs'
                                    type='success'
                                    icon={faRefresh}
                                    hoverText='Reset to Default Description'
                                    disabled={
                                        !displayDescription ||
                                        normalizeDescription(
                                            displayDescription
                                        ) ===
                                            normalizeDescription(
                                                autoGeneratedDescription
                                            )
                                    }
                                    onClick={fetchDescription}
                                />
                            </div>
                            <TextArea
                                value={displayDescription}
                                onChange={(e) => {
                                    setDisplayDescription(e.target.value);
                                }}
                                placeholder='No description available'
                                rows={5}
                            />
                            {mode !== Mode.EDIT && (
                                <p className='text-xs text-texts-secondary300 mt-1 italic'>
                                    ⚠️ Please do not modify text within curly
                                    braces {'{ }'} — they act as placeholders
                                    for default description generation.
                                </p>
                            )}
                        </div>
                    )}

                    {!selectedTool && (
                        <div className='text-sm text-texts-secondary300 my-2 px-2'>
                            Please select a tool from the dropdown
                        </div>
                    )}

                    {selectedTool?.config?.map((configField) => {
                        const isDropdownType = [
                            'repo_selector',
                            'template_selector',
                            'layout_selector',
                            'form_selector',
                            'dropdown',
                        ].includes(configField.type);
                        const isCategoryType =
                            configField.type === 'category_doc_manager';
                        const isToggleType = configField.type === 'toggle';
                        const isRequired = !OptionalConfigFields.includes(
                            configField.name.toLowerCase()
                        );

                        return (
                            <div
                                key={configField.name}
                                className={`p-2 ${
                                    isDropdownType
                                        ? 'flex flex-row items-center gap-5'
                                        : 'flex flex-col'
                                }`}
                            >
                                {!isToggleType && !isCategoryType && (
                                    <label className='text-base font-medium mb-1 text-texts-secondary300 '>
                                        {toSentenceCase(configField.name)}
                                        {isRequired && (
                                            <span className='text-texts-red400'>
                                                {' '}
                                                *
                                            </span>
                                        )}
                                    </label>
                                )}
                                {renderConfigField(configField)}
                            </div>
                        );
                    })}

                    <div className='flex items-center justify-between w-full mt-4'>
                        <Button
                            type='danger'
                            icon={faXmark}
                            className='w-1/3'
                            text='Cancel'
                            onClick={onClose}
                        />
                        <Button
                            type='success'
                            icon={mode === Mode.ADD ? faPlus : faPen}
                            className='w-1/3'
                            text={mode === Mode.ADD ? 'Create' : 'Update'}
                            onClick={handleSave}
                            disabled={
                                !instanceName ||
                                !toolId ||
                                !validateConfigFields(false) ||
                                (mode === Mode.EDIT && isUnchanged)
                            }
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
}
